<template>
  <div class="beauty-page" :style="pageStyle">
    <!-- 顶部导航栏 -->
    <van-nav-bar 
      :title="$t('choose.title')" 
      fixed
      safe-area-inset-top
      :style="navBarStyle"
    >
    </van-nav-bar>

    <!-- 内容区域 -->
    <div class="content-wrapper" :style="contentStyle">
      
      <!-- 地区筛选 -->
      <div class="filter-tabs">
        <div 
          class="filter-tab"
          :class="{ active: currentFilter === 'all' }"
          @click="changeFilter('all')"
        >
          {{ $t('common.all') }}
        </div>
        <div 
          v-for="area in areaList" 
          :key="area.id"
          class="filter-tab"
          :class="{ active: currentFilter === area.id }"
          @click="changeFilter(area.id)"
        >
          {{ area.name }}
        </div>
      </div>

      <!-- 列表内容 -->
      <div class="list-container">
        <van-pull-refresh 
          v-model="isRefreshing" 
          @refresh="onRefresh"
        >
          <!-- 网格视图 -->
          <div v-if="viewType === 1" class="grid-view">
            <div class="grid-column">
              <div 
                v-for="(item, index) in leftColumnList" 
                :key="`left_${item.id}_${index}`"
                class="grid-item"
                @click="goToProfile(item.id)"
              >
                <div class="simple-card">
                  <div class="card-image">
                    <img :src="item.image" :alt="item.class_name" />
                    <!-- 在线状态徽章 -->
                    <div class="online-badge" v-if="item.is_online">
                      <span class="online-dot"></span>
                      <span class="online-text">{{ $t('on line') }}</span>
                    </div>
                  </div>
                  <div class="card-content">
                    <div class="tags">
                      <span class="tag" v-if="item.tags_arr && item.tags_arr[0]">
                        {{ item.tags_arr[0] }}
                      </span>
                    </div>
                    <div class="card-footer">
                      <div class="user-info">
                        <div class="avatar-badge"></div>
                        <span class="username">{{ item.xuanfei_name }}</span>
                      </div>
                      <div class="popularity">
                        <van-icon name="like" size="12" color="#dc63a1" />
                        {{ item.renqi || 0 }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="grid-column">
              <div 
                v-for="(item, index) in rightColumnList" 
                :key="`right_${item.id}_${index}`"
                class="grid-item"
                @click="goToProfile(item.id)"
              >
                <div class="simple-card">
                  <div class="card-image">
                    <img :src="item.image" :alt="item.class_name" />
                    <!-- 在线状态徽章 -->
                    <div class="online-badge" v-if="item.is_online">
                      <span class="online-dot"></span>
                      <span class="online-text">{{ $t('on line') }}</span>
                    </div>
                  </div>
                  <div class="card-content">
                    <div class="tags">
                      <span class="tag" v-if="item.tags_arr && item.tags_arr[0]">
                        {{ item.tags_arr[0] }}
                      </span>
                    </div>
                    <div class="card-footer">
                      <div class="user-info">
                        <div class="avatar-badge"></div>
                        <span class="username">{{ item.xuanfei_name }}</span>
                      </div>
                      <div class="popularity">
                        <van-icon name="like" size="12" color="#dc63a1" />
                        {{ item.renqi || 0 }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 列表视图 -->
          <div v-else class="list-view">
            <div 
              v-for="(item, index) in beautyList" 
              :key="`list_${item.id}_${index}`"
              class="list-item"
              @click="goToProfile(item.id)"
            >
              <div class="simple-list-card">
                <div class="list-left">
                  <div class="image-container">
                    <img :src="item.image" :alt="item.class_name" />
                    <!-- 列表视图在线状态 -->
                    <div class="online-badge list-online-badge" v-if="item.is_online">
                      <span class="online-dot"></span>
                      <span class="online-text">{{ $t('on line') }}</span>
                    </div>
                  </div>
                </div>
                <div class="list-right">
                  <div class="list-header">
                    <div class="badges">
                      <span class="badge">
                        <van-icon name="vip" size="12" />
                        {{ $t('home.smrz') }}
                      </span>
                      <span class="badge">
                        <van-icon name="video" size="12" />
                        {{ $t('home.videorz') }}
                      </span>
                    </div>
                    <div class="location">{{ item.class_name }}</div>
                  </div>
                  <div class="list-tags">
                    <span 
                      v-for="(tag, tagIndex) in item.tags_arr" 
                      :key="tagIndex"
                      class="tag"
                      :class="`tag-${(tagIndex % 3) + 1}`"
                    >
                      {{ tag }}
                    </span>
                  </div>
                  <div class="list-info">
                    <div class="star-rating">
                      {{ $t('home.star') }}：
                      <span 
                        v-for="(star, starIndex) in item.star" 
                        :key="starIndex"
                        class="star"
                      ></span>
                    </div>
                    <div class="user-details">
                      {{ $t('home.sg') }}: {{ item.sg }}cm 
                      {{ $t('home.xw') }}：{{ item.sw }}
                      {{ $t('home.diqu') }}：{{ item.class_name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <van-empty
            v-if="!isLoading && beautyList.length === 0"
            :description="$t('common.noData')"
            class="empty-state"
          />

          <!-- 加载更多箭头 - 去掉文字 -->
          <div v-if="!isFinished && beautyList.length > 0" class="load-more-container">
            <div class="load-more-arrow" @click="loadMore">
              <div class="arrow-icon" :class="{ loading: isLoading }">
                <van-icon name="arrow-down" size="24" />
              </div>
            </div>
          </div>

          <!-- 没有更多数据提示 -->
          <div v-if="isFinished && beautyList.length > 0" class="no-more-container">
            <div class="no-more-text">
              {{ $t('common.nomore') }}
            </div>
          </div>
        </van-pull-refresh>
      </div>
    </div>

    <!-- 返回顶部按钮 -->
    <div class="back-to-top" :class="{ show: showBackTop }" @click="scrollToTop">
      <van-icon name="back-top" size="24" />
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant';

export default {
  name: 'BeautyPage',
  data() {
    return {
      viewType: 1,
      currentFilter: 'all',
      areaList: [],
      beautyList: [],
      currentPage: 1,
      totalCount: 0,
      pageSize: 10,
      isLoading: false,
      isRefreshing: false,
      isFinished: false,
      showBackTop: false,
      safeAreaInsetTop: '0px',
      // 模拟在线状态数据（实际应该从API获取）
      mockOnlineStatus: [true, false, true, true, false, true, false, true, true, false]
    };
  },
  computed: {
    // 网格视图左右分栏
    leftColumnList() {
      return this.beautyList.filter((_, index) => index % 2 === 0);
    },
    rightColumnList() {
      return this.beautyList.filter((_, index) => index % 2 === 1);
    },
    // 导航栏样式 - 动态计算安全区域
    navBarStyle() {
      return {
        paddingTop: this.safeAreaInsetTop,
        height: `calc(46px + ${this.safeAreaInsetTop})`
      };
    },
    // 内容区域样式 - 动态计算边距
    contentStyle() {
      return {
        marginTop: `calc(46px + ${this.safeAreaInsetTop})`,
        paddingTop: '12px'
      };
    },
    // 页面样式
    pageStyle() {
      return {
        paddingTop: this.safeAreaInsetTop
      };
    }
  },
  methods: {
    /**
     * 安全的获取滚动位置 - 使用 window
     */
    scrollTops() {
      return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    },

    /**
     * 处理滚动显示返回顶部按钮
     */
    handleScroll() {
      const scrollTop = this.scrollTops();
      this.showBackTop = scrollTop > 300;
    },

    /**
     * 返回顶部
     */
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    /**
     * 获取安全区域高度
     */
    getSafeAreaInsetTop() {
      // 获取安全区域顶部间距
      const safeArea = getComputedStyle(document.documentElement).getPropertyValue('--sat') || 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top') ||
                      'env(safe-area-inset-top)';
      return safeArea;
    },

    /**
     * 切换视图类型
     */
    switchView(type) {
      this.viewType = type;
    },

    /**
     * 切换地区筛选
     */
    changeFilter(filterId) {
      this.currentFilter = filterId;
      this.currentPage = 1;
      this.beautyList = [];
      this.isFinished = false;
      this.getBeautyList(true);
    },

    /**
     * 获取地区列表
     */
    async getAreaList() {
      try {
        const response = await this.$http({
          method: 'get',
          url: 'address_list'
        });
        this.areaList = response.data;
      } catch (error) {
        console.error('获取地区列表失败:', error);
        Toast(this.$t('common.loadFailed'));
      }
    },

    /**
     * 获取美女列表 - 添加在线状态处理
     */
    async getBeautyList(reset = false) {
      if (this.isLoading) return;
      
      if (reset) {
        this.currentPage = 1;
        this.beautyList = [];
        this.isFinished = false;
      }

      this.isLoading = true;

      try {
        const response = await this.$http({
          method: 'get',
          url: 'xuanfeilist',
          data: {
            id: this.currentFilter,
            page: this.currentPage,
            limit: this.pageSize
          }
        });

        const newData = response.data.data || [];
        
        // 为数据添加在线状态（实际项目中应该由后端返回）
        const processedData = newData.map((item, index) => ({
          ...item,
          is_online: this.mockOnlineStatus[index % this.mockOnlineStatus.length] || false
        }));
        
        if (reset) {
          this.beautyList = processedData;
        } else {
          this.beautyList = [...this.beautyList, ...processedData];
        }

        this.totalCount = response.data.total || 0;
        this.currentPage++;

        // 检查是否已加载所有数据
        if (this.beautyList.length >= this.totalCount || newData.length < this.pageSize) {
          this.isFinished = true;
        }

      } catch (error) {
        console.error('获取列表失败:', error);
        Toast(this.$t('common.loadFailed'));
      } finally {
        this.isLoading = false;
        this.isRefreshing = false;
      }
    },

    /**
     * 加载更多
     */
    async loadMore() {
      if (!this.isFinished && !this.isLoading) {
        await this.getBeautyList();
      }
    },

    /**
     * 下拉刷新
     */
    onRefresh() {
      this.getBeautyList(true);
      Toast(this.$t('common.refresh'));
    },

    /**
     * 跳转到详情页
     */
    goToProfile(id) {
      this.$router.push({ path: '/profile?id=' + id });
    }
  },
  async created() {
    await Promise.all([
      this.getAreaList(),
      this.getBeautyList(true)
    ]);
  },
  mounted() {
    // 设置安全区域
    this.safeAreaInsetTop = this.getSafeAreaInsetTop();
    
    // 使用 window 的滚动监听
    window.addEventListener('scroll', this.handleScroll);
    
    // 监听窗口变化
    window.addEventListener('resize', () => {
      this.safeAreaInsetTop = this.getSafeAreaInsetTop();
    });
  },
  beforeDestroy() {
    // 移除事件监听
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', () => {});
  }
};
</script>

<style lang="less" scoped>
.beauty-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  /* 设置安全区域变量 */
  --safe-area-inset-top: env(safe-area-inset-top, 0px);
  --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
}

/* 导航栏样式 - 修复定位问题 */
.van-nav-bar {
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  /* 动态高度由 computed 控制 */
  
  :deep(.van-nav-bar__title) {
    color: white;
    font-weight: 600;
    font-size: 40px;
  }
  
  :deep(.van-nav-bar__right) {
    .view-toggle {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .van-icon {
        padding: 6px;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.2);
        transition: all 0.3s ease;
        
        &.active {
          background: rgba(255, 255, 255, 0.4);
          transform: scale(1.1);
        }
      }
    }
  }
}

/* 内容区域 - 修复边距问题 */
.content-wrapper {
  /* 动态边距由 computed 控制 */
  padding: 0 12px 80px;
  min-height: calc(100vh - 46px - var(--safe-area-inset-top, 0px));
}

/* 筛选标签 */
.filter-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0;
  padding: 0 4px;
}

.filter-tab {
  padding: 8px 16px;
  background: white;
  border-radius: 20px;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e8e8e8;
  white-space: nowrap;
  
  &:hover {
    background: #f8f9fa;
    transform: translateY(-1px);
  }
  
  &.active {
    background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
    color: white;
    border-color: transparent;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
}

/* 列表容器 */
.list-container {
  min-height: 400px;
}

/* 网格视图 */
.grid-view {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 8px 0;
}

.grid-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.grid-item {
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

/* 简单卡片样式 */
.simple-card {
  width: 100%;
  
  .card-image {
    position: relative;
    width: 100%;
    aspect-ratio: 3/4;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .card-content {
    padding: 12px;
    
    .tags {
      margin-bottom: 8px;
      
      .tag {
        background: #3498db;
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 28px;
      }
    }
    
    .card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 22px;
      color: #666;
      
      .user-info {
        display: flex;
        align-items: center;
        gap: 6px;
        
        .avatar-badge {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #efba8f;
        }
      }
      
      .popularity {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }
}

/* 绿色在线徽章样式 - 热门公告区域 */
.online-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 4px 8px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  animation: onlinePulse 2s infinite;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(34, 197, 94, 0.2);
  z-index: 1000;
}

.online-dot {
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-radius: 50%;
  margin-right: 4px;
  animation: dotPulse 1.5s infinite;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
}

.online-text {
  font-size: 11px;
  font-weight: 600;
  color: #15803d;
  white-space: nowrap;
}

/* 列表视图在线徽章调整 */
.list-online-badge {
  top: 4px;
  right: 4px;
  padding: 3px 6px;
  
  .online-dot {
    width: 6px;
    height: 6px;
    margin-right: 3px;
  }
  
  .online-text {
    font-size: 10px;
  }
}

/* 在线徽章动画 */
@keyframes onlinePulse {
  0% {
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  }
  50% {
    box-shadow: 0 2px 12px rgba(34, 197, 94, 0.5);
  }
  100% {
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  }
}

@keyframes dotPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 0 10px rgba(34, 197, 94, 0.8);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
  }
}

/* 列表视图 */
.list-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.list-item {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

/* 简单列表卡片样式 */
.simple-list-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  
  .list-left {
    flex-shrink: 0;
    
    .image-container {
      position: relative;
      width: 80px;
      height: 80px;
      border-radius: 8px;
      overflow: hidden;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }
  
  .list-right {
    flex: 1;
    
    .list-header {
      margin-bottom: 8px;
      
      .badges {
        display: flex;
        gap: 6px;
        margin-bottom: 6px;
        
        .badge {
          background: linear-gradient(135deg, #a8edea, #fed6e3);
          color: #2d3748;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 24px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }
      
      .location {
        font-size: 30px;
        font-weight: 600;
        color: #2d3748;
      }
    }
    
    .list-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 12px;
      
      .tag {
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 20px;
        color: white;
        font-weight: 500;
        
        &.tag-1 {
          background: #fe257f;
        }
        &.tag-2 {
          background: #ff9702;
        }
        &.tag-3 {
          background: #0fa7fe;
        }
      }
    }
    
    .list-info {
      .star-rating {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 22px;
        color: #e53e3e;
        margin-bottom: 4px;
        
        .star {
          width: 12px;
          height: 12px;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="%23f6ad55" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>') no-repeat;
        }
      }
      
      .user-details {
        font-size: 28px;
        color: #718096;
        line-height: 1.4;
      }
    }
  }
}

/* 空状态 */
.empty-state {
  padding: 60px 20px;
  
  :deep(.van-empty__description) {
    color: #a0aec0;
    font-size: 14px;
  }
}

/* 加载更多箭头 - 只有箭头没有文字 */
.load-more-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
  margin-top: 16px;
}

.load-more-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  width: 60px;
  height: 60px;
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
  }
  
  &:active {
    transform: translateY(0) scale(1);
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.4);
  }
}

.arrow-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  
  &.loading {
    animation: rotate 1s linear infinite;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 没有更多数据提示 */
.no-more-container {
  text-align: center;
  padding: 32px 20px;
  color: #a0aec0;
}

.no-more-text {
  font-size: 14px;
}

/* 返回顶部按钮 */
.back-to-top {
  position: fixed;
  right: 20px;
  bottom: 80px;
  z-index: 999;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3498db 0%, #8e44ad 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0;
  visibility: hidden;
  
  &.show {
    opacity: 1;
    visibility: visible;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
  }
}

/* 响应式设计 */
@media (max-width: 375px) {
  .content-wrapper {
    padding: 0 10px 70px;
  }
  
  .filter-tabs {
    gap: 6px;
  }
  
  .filter-tab {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .grid-view {
    gap: 10px;
  }
  
  .load-more-arrow {
    width: 56px;
    height: 56px;
    padding: 14px;
  }
  
  .simple-list-card {
    padding: 12px;
    
    .list-left .image-container {
      width: 70px;
      height: 70px;
    }
  }
  
  /* 在线徽章响应式调整 */
  .online-badge {
    padding: 3px 6px;
    
    .online-text {
      font-size: 10px;
    }
  }
  
  .list-online-badge {
    padding: 2px 4px;
    
    .online-text {
      font-size: 9px;
    }
  }
}

@media (min-width: 768px) {
  .content-wrapper {
    padding: 0 16px 80px;
  }
  
  .grid-view {
    gap: 16px;
  }
  
  .list-view {
    gap: 16px;
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .beauty-page {
    background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  }
  
  .filter-tab {
    background: #2d3748;
    color: #e2e8f0;
    border-color: #4a5568;
    
    &:hover {
      background: #4a5568;
    }
  }
  
  .grid-item,
  .list-item,
  .simple-card,
  .simple-list-card {
    background: #2d3748;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  
  .simple-card .card-content .card-footer,
  .simple-list-card .list-right .list-header .location {
    color: #e2e8f0;
  }
  
  .simple-list-card .list-right .list-info .user-details {
    color: #a0aec0;
  }
  
  /* 深色模式在线徽章适配 */
  .online-badge {
    background: rgba(255, 255, 255, 0.1) !important;
    border-color: rgba(34, 197, 94, 0.4) !important;
    
    .online-text {
      color: #22c55e !important;
    }
  }
}

/* 无障碍访问支持 */
@media (prefers-reduced-motion: reduce) {
  .grid-item,
  .list-item,
  .filter-tab,
  .load-more-arrow,
  .back-to-top {
    transition: none;
  }
  
  .arrow-icon.loading {
    animation: none;
  }
  
  .online-badge,
  .online-dot {
    animation: none !important;
  }
}

/* 紧急修复：如果还是看不见，使用这个强制方案 */
.beauty-page.force-fix {
  padding-top: env(safe-area-inset-top, 0px);
}

.content-wrapper.force-fix {
  margin-top: 0 !important;
  padding-top: calc(46px + env(safe-area-inset-top, 0px)) !important;
}
</style>